import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Short labels for columns (space-constrained)
const SHORT = ['Case\nBased', 'Gen.\nAlgos', 'Neural\nNets', 'Prob.\nMethods', 'RL', 'Rule\nLrng', 'Theory'];
// Full labels for rows and tooltip
const FULL  = ['Case Based', 'Genetic Algorithms', 'Neural Networks', 'Probabilistic Methods', 'Reinforcement Learning', 'Rule Learning', 'Theory'];

// Real GAT confusion matrix (rows = true, cols = predicted)
const MATRIX = [
  [109,  3,  5,  2,  4,  1,  6],
  [  1,125,  3,  0,  1,  5,  1],
  [  5,  4,192, 11,  2,  0,  8],
  [  0,  2,  7,102,  1,  1,  4],
  [  2,  0,  3,  1, 95,  0,  0],
  [  3,  7,  0,  0,  0,108,  1],
  [  5,  2, 12,  5,  2,  0,131],
];

const ROW_SUMS = MATRIX.map(row => row.reduce((a, b) => a + b, 0));

// Per-class accuracy for the legend
const PER_CLASS_ACC = MATRIX.map((row, i) =>
  ((row[i] / ROW_SUMS[i]) * 100).toFixed(0) + '%'
);

const CELL = 44; // px per cell

export const ConfusionMatrix: React.FC = () => {
  const [hovered, setHovered] = useState<{ r: number; c: number } | null>(null);

  const cellBg = (val: number, r: number, c: number) => {
    if (val === 0) return 'rgba(255,255,255,0.03)';
    const norm = val / ROW_SUMS[r];
    if (r === c) return `rgba(16,185,129,${Math.max(0.15, norm * 0.95)})`;
    return `rgba(244,63,94,${Math.max(0.06, norm * 2.5)})`;
  };

  const cellText = (val: number, r: number, c: number) => {
    if (val === 0) return 'rgba(255,255,255,0.2)';
    const norm = val / ROW_SUMS[r];
    return norm > 0.35 ? '#ffffff' : r === c ? '#6ee7b7' : '#fca5a5';
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Overall accuracy badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)',
          borderRadius: 999, padding: '4px 12px',
          fontSize: '0.75rem', fontWeight: 700, color: '#6ee7b7',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
          GAT Model — Test Accuracy 82.9%
        </div>
        <span style={{ fontSize: '0.7rem', color: '#839493' }}>
          Rows = True class &nbsp;·&nbsp; Cols = Predicted class
        </span>
      </div>

      {/* Scrollable matrix wrapper */}
      <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <div style={{ display: 'inline-flex', gap: 0, minWidth: 'max-content' }}>

          {/* Y-axis label */}
          <div style={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 18, fontSize: '0.65rem', fontWeight: 700,
            color: '#839493', letterSpacing: '0.06em', textTransform: 'uppercase',
            flexShrink: 0, marginRight: 4,
          }}>
            True Label
          </div>

          <div>
            {/* Column headers */}
            <div style={{ display: 'flex', marginLeft: 96, marginBottom: 4 }}>
              {SHORT.map((name, i) => (
                <div key={i} style={{
                  width: CELL, flexShrink: 0,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'flex-end', paddingBottom: 4,
                  fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600,
                  textAlign: 'center', lineHeight: 1.3, height: 52,
                }}>
                  {name.split('\n').map((l, j) => <span key={j}>{l}</span>)}
                </div>
              ))}
            </div>

            {/* Rows */}
            {MATRIX.map((row, r) => (
              <div key={r} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                {/* Row label */}
                <div style={{
                  width: 96, flexShrink: 0,
                  textAlign: 'right', paddingRight: 8,
                  fontSize: '0.65rem', color: '#b9cac9', fontWeight: 600,
                  borderRight: '1px solid rgba(131,148,147,0.15)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {FULL[r]}
                </div>

                {/* Cells */}
                {row.map((val, c) => {
                  const isHov = hovered?.r === r && hovered?.c === c;
                  return (
                    <motion.div
                      key={c}
                      onMouseEnter={() => setHovered({ r, c })}
                      onMouseLeave={() => setHovered(null)}
                      whileHover={{ scale: 1.15, zIndex: 20 }}
                      style={{
                        position: 'relative',
                        width: CELL, height: CELL, flexShrink: 0,
                        margin: 1,
                        borderRadius: 6,
                        background: cellBg(val, r, c),
                        border: r === c ? '1px solid rgba(16,185,129,0.3)' : '1px solid transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: val > 99 ? '0.62rem' : '0.7rem',
                        fontFamily: 'monospace', fontWeight: 700,
                        color: cellText(val, r, c),
                        cursor: 'default',
                        boxShadow: r === c ? '0 0 8px rgba(16,185,129,0.15)' : 'none',
                      }}
                    >
                      {val}

                      {/* Tooltip */}
                      {isHov && (
                        <div style={{
                          position: 'absolute',
                          top: -72, left: '50%', transform: 'translateX(-50%)',
                          background: '#1e293b',
                          border: '1px solid rgba(131,148,147,0.25)',
                          borderRadius: 10, padding: '6px 10px',
                          fontSize: '0.68rem', color: '#f1f5f9',
                          whiteSpace: 'nowrap', zIndex: 50,
                          pointerEvents: 'none',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                          lineHeight: 1.6,
                        }}>
                          <div><span style={{ color: '#94a3b8' }}>True:</span> {FULL[r]}</div>
                          <div><span style={{ color: '#94a3b8' }}>Pred:</span> {FULL[c]}</div>
                          <div style={{ color: r === c ? '#6ee7b7' : '#fca5a5', fontWeight: 700 }}>
                            {val} samples ({((val / ROW_SUMS[r]) * 100).toFixed(1)}%)
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}

                {/* Per-row accuracy */}
                <div style={{
                  marginLeft: 8, fontSize: '0.65rem',
                  fontFamily: 'monospace', fontWeight: 700,
                  color: '#10b981', width: 36,
                }}>
                  {PER_CLASS_ACC[r]}
                </div>
              </div>
            ))}

            {/* X-axis label */}
            <div style={{
              textAlign: 'center', marginTop: 10, marginLeft: 96,
              fontSize: '0.65rem', fontWeight: 700, color: '#839493',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              Predicted Label
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', color: '#94a3b8' }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(16,185,129,0.7)', border: '1px solid rgba(16,185,129,0.4)' }} />
          Correct prediction
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', color: '#94a3b8' }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: 'rgba(244,63,94,0.5)' }} />
          Misclassification
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', color: '#94a3b8' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#10b981' }}>%</span>
          Per-class accuracy
        </div>
      </div>
    </div>
  );
};
